import {
  AccountBalance,
  AccountTreeOutlined,
  Assessment,
  AutoAwesomeOutlined,
  BugReportOutlined,
  Business,
  CreditCard,
  Email,
  Insights,
  LockOpen,
  MenuBook,
  NotificationsActiveOutlined,
  OndemandVideoOutlined,
  People,
  PointOfSale,
  Security,
  SubdirectoryArrowRight,
  SupportAgent,
  TextSnippet,
  TrendingUp,
} from "@mui/icons-material";
import React, { lazy } from "react";
import { matchPath } from "react-router";

import type { AdminPermissionKey } from "./adminPermissionKeys";

import { adminUiCopy } from "@/copy/adminUiCopy";
import type { EntryPointOverlapKey } from "@/store/api/endpoints/adminV2EntryPointOverlapApi";
import type { AdminV2RevenueMenuVisibilityKey } from "@/types/adminV2";
import { isRevenueMenuVisibilityEnabled } from "@/utils/revenueAccessVisibility";
import { getEntryPointOverlapTitle } from "@/vnext/routes/insights/entry-points/entryPointOverlapLabels";
import AdminAccessAuditRoute from "@/vnext/routes/settings/admin-access/AdminAccessAuditRoute";
import AiInsightsRoute from "@/vnext/routes/settings/ai-insights/AiInsightsRoute";
import ApprovalsRoute from "@/vnext/routes/settings/approvals/ApprovalsRoute";
import AuditLogRoute from "@/vnext/routes/settings/audit-log/AuditLogRoute";
import BusinessTypesRoute from "@/vnext/routes/settings/business-types/BusinessTypesRoute";
import EditableContentBlockRoute from "@/vnext/routes/settings/content-block/EditableContentBlockRoute";
import CsvExportsRoute from "@/vnext/routes/settings/csv-exports/CsvExportsRoute";
import DemoAccessRoute from "@/vnext/routes/settings/demo-access/DemoAccessRoute";
import DemoOpsRoute from "@/vnext/routes/settings/demo-ops/DemoOpsRoute";
import DocsShareLinksRoute from "@/vnext/routes/settings/docs-share-links/DocsShareLinksRoute";
import EntitlementsRoute from "@/vnext/routes/settings/entitlements/EntitlementsRoute";
import FeatureFlagsRoute from "@/vnext/routes/settings/feature-flags/FeatureFlagsRoute";
import GuidesRoute from "@/vnext/routes/settings/guides/GuidesRoute";
import IntegrationsRoute from "@/vnext/routes/settings/integrations/IntegrationsRoute";
import JobsRoute from "@/vnext/routes/settings/jobs/JobsRoute";
import SystemArchitectureRoute from "@/vnext/routes/settings/system-architecture/SystemArchitectureRoute";
import TeamPermissionsRoute from "@/vnext/routes/settings/team-permissions/TeamPermissionsRoute";
import WebhookLogsRoute from "@/vnext/routes/settings/webhook-logs/WebhookLogsRoute";

/**
 * A single source of truth for:
 * - route paths + human labels (used by breadcrumbs and search)
 * - access gates (permissions and revenue menu visibility)
 * - route page boundaries (usually lazy-loaded to reduce initial bundle size)
 *
 * @remarks
 * This module intentionally keeps most route pages lazy-loaded so:
 * - the route table remains central and stable
 * - pages are code-split and loaded on demand
 * - we avoid dependency cycles between `PageLayout` (breadcrumbs) and pages
 *
 * A small number of settings/product routes are imported eagerly because their
 * shared workspace shell is itself part of the route-resolution graph, and
 * live admin verification showed these pages could stay trapped behind the
 * generic route loading fallback in dev when a second lazy boundary was kept
 * here.
 */

export const ADMIN_EAGER_ROUTE_EXCEPTION_IDS = [
  "v2_settings_webhook_logs",
  "v2_team_permissions",
  "v2_approvals",
  "v2_audit_log",
  "admin_access_audit",
  "business_types",
  "docs_share_links",
  "csv_exports",
  "guides",
  "guide_details",
  "editable_content_block",
  "demo_access",
  "demo_ops",
  "v2_jobs_monitor",
  "v2_ai_insights_access",
  "v2_feature_flags",
  "v2_feature_flags_product_alias",
  "v2_entitlements",
  "v2_settings_integrations",
  "system_architecture",
] as const;

export type AdminEagerRouteExceptionId =
  (typeof ADMIN_EAGER_ROUTE_EXCEPTION_IDS)[number];

export type AdminNavGroupId =
  | "people"
  | "growth"
  | "sales"
  | "product"
  | "support"
  | "learning"
  | "revenue"
  | "insights"
  | "settings"
  | "advanced"
  | "settings_v2"
  | "users_orgs"
  | "billing"
  | "email"
  | "integrations"
  | "impersonation"
  | "analytics"
  | "observability"
  | "dev_tools"
  | "admin";

export interface AdminNavGroup {
  id: AdminNavGroupId;
  label: string;
  icon: React.ReactNode;
  defaultExpanded?: boolean;
}

export interface AdminRouteDefinition {
  id: string;
  /**
   * React Router path pattern (we use absolute paths for consistency).
   */
  path: string;
  /**
   * Human-friendly label used in route titles and breadcrumbs.
   */
  label: string;
  /**
   * Optional shorter label for the sidebar when the route title needs more
   * context than the main navigation should carry.
   */
  sidebarLabel?: string;
  icon: React.ReactNode;
  /**
   * `null` means this is a top-level route (e.g. Dashboard).
   */
  navGroup: AdminNavGroupId | null;
  /**
   * Lower numbers appear earlier within the same group.
   */
  navOrder: number;
  /**
   * Hidden routes are still reachable (deep links / internal navigation) but
   * do not appear in the sidebar.
   */
  navHidden?: boolean;
  /**
   * Route page component, usually lazy-loaded.
   */
  Component:
    React.ComponentType | React.LazyExoticComponent<React.ComponentType>;
  /**
   * Optional RBAC gate for route access + sidebar visibility.
   *
   * @remarks
   * The route table is treated as a deny-by-default configuration surface. If a route declares
   * a permission key but the caller does not provide `permissionKeys`, we assume identity is
   * still loading and hide the route from navigation.
   */
  requiredPermission?: AdminPermissionKey;
  /**
   * Additional permission keys that can satisfy this route's access gate while
   * keeping `requiredPermission` as the canonical parity/audit anchor.
   *
   * @remarks
   * Use sparingly for owner-surface migrations where an approved owner surface
   * absorbs historical work from older permission domains. The canonical
   * permission remains the first key recorded for route parity and approval
   * requests, while these fallbacks keep existing operators from losing the
   * workflow during the consolidation.
   */
  requiredPermissionFallbacks?: AdminPermissionKey[];
  /**
   * Optional menu visibility gate resolved from `/admin/v2/revenue/access-context`.
   *
   * @remarks
   * Menu visibility is also treated as deny-by-default: if the visibility object is not yet
   * available, these routes should not appear in navigation.
   */
  requiredMenuVisibility?: AdminV2RevenueMenuVisibilityKey;
}

export interface SidebarNavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  activeMatchPaths?: string[];
  badge?: number;
  description?: string;
  children?: SidebarNavigationItem[];
  disabled?: boolean;
  defaultExpanded?: boolean;
}

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    id: "people",
    label: "CRM",
    icon: <Business />,
    defaultExpanded: true,
  },
  {
    id: "growth",
    label: "Messaging",
    icon: <TrendingUp />,
  },
  {
    id: "sales",
    label: "Sales",
    icon: <PointOfSale />,
  },
  {
    id: "product",
    label: "Product",
    icon: <Assessment />,
  },
  {
    id: "support",
    label: "Support",
    icon: <SupportAgent />,
  },
  {
    id: "learning",
    label: "Help",
    icon: <MenuBook />,
  },
  {
    id: "revenue",
    label: "Revenue",
    icon: <CreditCard />,
  },
  {
    id: "insights",
    label: "Insights",
    icon: <Insights />,
  },
  {
    id: "settings",
    label: "Administration",
    icon: <Security />,
  },
];

// CRITICAL: each fixed overlap is a literal registry entry so release proof
// can account for every distinct card pair without accepting dynamic routes.
// https://reactrouter.com/start/declarative/routing
const entryPointOverlapComponent = (key: EntryPointOverlapKey) =>
  lazy(() =>
    import("@/vnext/routes/crm/contacts/EntryPointOverlapRoute").then(
      ({ default: OverlapRoute }) => ({
        default: () => <OverlapRoute overlapKey={key} />,
      }),
    ),
  );

const RAW_ADMIN_ROUTES: AdminRouteDefinition[] = [
  {
    id: "dashboard_entry",
    path: "/",
    label: "Dashboard",
    icon: <Insights />,
    navGroup: null,
    navOrder: 0,
    navHidden: true,
    Component: lazy(
      () => import("@/vnext/routes/dashboard/DashboardEntryRoute"),
    ),
  },
  {
    id: "users",
    path: "/users",
    label: "CRM users",
    sidebarLabel: "Users",
    icon: <People />,
    navGroup: "people",
    navOrder: 8,
    requiredPermission: "support.view",
    Component: lazy(() => import("@/vnext/routes/people/users/UsersRoute")),
  },
  {
    id: "user_details",
    path: "/users/:userId",
    label: "User profile",
    icon: <People />,
    navGroup: "people",
    navOrder: 8.1,
    navHidden: true,
    requiredPermission: "support.view",
    Component: lazy(
      () => import("@/vnext/routes/people/users/UserDetailRoute"),
    ),
  },
  {
    id: "organisations",
    path: "/organisations",
    label: "CRM organisations",
    sidebarLabel: "Organisations",
    icon: <Business />,
    navGroup: "people",
    navOrder: 9,
    requiredPermission: "organisations.access_all",
    Component: lazy(
      () => import("@/vnext/routes/people/organisations/OrganisationsRoute"),
    ),
  },
  {
    id: "organisation_details",
    path: "/organisations/:orgId",
    label: "Organisation profile",
    icon: <Business />,
    navGroup: "people",
    navOrder: 9.1,
    navHidden: true,
    requiredPermission: "organisations.access_all",
    Component: lazy(
      () =>
        import("@/vnext/routes/people/organisations/OrganisationDetailRoute"),
    ),
  },
  {
    id: "partners",
    path: "/partners",
    label: "CRM partners",
    sidebarLabel: "Partners",
    icon: <People />,
    navGroup: "people",
    navOrder: 9.2,
    requiredPermission: "organisations.access_all",
    Component: lazy(
      () => import("@/vnext/routes/people/partners/PartnersRoute"),
    ),
  },
  {
    id: "partner_details",
    path: "/partners/:partnerId",
    label: "Partner profile",
    icon: <People />,
    navGroup: "people",
    navOrder: 9.3,
    navHidden: true,
    requiredPermission: "organisations.access_all",
    Component: lazy(
      () => import("@/vnext/routes/people/partners/PartnerDetailRoute"),
    ),
  },
  {
    id: "v2_crm",
    path: "/crm",
    label: "CRM source of truth",
    sidebarLabel: "All contacts",
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/contacts/CrmContactsRoute"),
    ),
  },
  {
    id: "v2_crm_contacts_path_alias",
    path: "/crm/contacts",
    label: "CRM contacts",
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/contacts/CrmContactsRoute"),
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_health-did-not-connect--connected",
    path: "/crm/entry-point-overlaps/health-did-not-connect--connected",
    label: getEntryPointOverlapTitle("health-did-not-connect--connected"),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent("health-did-not-connect--connected"),
  },
  {
    id: "v2_crm_entry_point_overlap_health-did-not-generate--report-sent",
    path: "/crm/entry-point-overlaps/health-did-not-generate--report-sent",
    label: getEntryPointOverlapTitle("health-did-not-generate--report-sent"),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "health-did-not-generate--report-sent",
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_health-not-opened-offer--saw-offer",
    path: "/crm/entry-point-overlaps/health-not-opened-offer--saw-offer",
    label: getEntryPointOverlapTitle("health-not-opened-offer--saw-offer"),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent("health-not-opened-offer--saw-offer"),
  },
  {
    id: "v2_crm_entry_point_overlap_health-did-not-start-checkout--left-checkout",
    path: "/crm/entry-point-overlaps/health-did-not-start-checkout--left-checkout",
    label: getEntryPointOverlapTitle(
      "health-did-not-start-checkout--left-checkout",
    ),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "health-did-not-start-checkout--left-checkout",
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_health-not-opened-offer--trial-started",
    path: "/crm/entry-point-overlaps/health-not-opened-offer--trial-started",
    label: getEntryPointOverlapTitle("health-not-opened-offer--trial-started"),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "health-not-opened-offer--trial-started",
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_health-did-not-start-checkout--trial-started",
    path: "/crm/entry-point-overlaps/health-did-not-start-checkout--trial-started",
    label: getEntryPointOverlapTitle(
      "health-did-not-start-checkout--trial-started",
    ),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "health-did-not-start-checkout--trial-started",
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_health-left-checkout--trial-started",
    path: "/crm/entry-point-overlaps/health-left-checkout--trial-started",
    label: getEntryPointOverlapTitle("health-left-checkout--trial-started"),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "health-left-checkout--trial-started",
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_health-card-failed--did-not-continue",
    path: "/crm/entry-point-overlaps/health-card-failed--did-not-continue",
    label: getEntryPointOverlapTitle("health-card-failed--did-not-continue"),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "health-card-failed--did-not-continue",
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_trial-card-failed--did-not-continue",
    path: "/crm/entry-point-overlaps/trial-card-failed--did-not-continue",
    label: getEntryPointOverlapTitle("trial-card-failed--did-not-continue"),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "trial-card-failed--did-not-continue",
    ),
  },
  {
    id: "v2_crm_entry_point_overlap_trial-signups-only-card-failed--did-not-continue",
    path: "/crm/entry-point-overlaps/trial-signups-only-card-failed--did-not-continue",
    label: getEntryPointOverlapTitle(
      "trial-signups-only-card-failed--did-not-continue",
    ),
    icon: <Business />,
    navGroup: "people",
    navOrder: 10,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: entryPointOverlapComponent(
      "trial-signups-only-card-failed--did-not-continue",
    ),
  },
  {
    id: "v2_crm_duplicates",
    path: "/crm/duplicates",
    label: "Duplicate contacts",
    icon: <Business />,
    navGroup: "people",
    navOrder: 10.2,
    // Keep the workbench on dedicated hidden routes so guides and notices
    // can deep-link operators straight into the correct Sprint 3 surface.
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/duplicates/CrmDuplicatesRoute"),
    ),
  },
  {
    id: "v2_crm_identity_review",
    path: "/crm/identity-review",
    label: "Identity review",
    icon: <Business />,
    navGroup: "people",
    navOrder: 10.3,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/identity-review/CrmIdentityReviewRoute"),
    ),
  },
  {
    id: "v2_crm_contacts_import",
    path: "/crm/contacts/import",
    label: "Import contacts",
    icon: <Business />,
    navGroup: "people",
    navOrder: 10.5,
    navHidden: true,
    requiredPermission: "crm.edit",
    Component: lazy(
      () => import("@/vnext/routes/crm/import/ContactsImportRoute"),
    ),
  },
  {
    id: "v2_crm_contact_record",
    path: "/crm/contacts/:contactId",
    label: "Contact",
    icon: <Business />,
    navGroup: "people",
    navOrder: 11,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/contacts/ContactRecordRoute"),
    ),
  },
  {
    id: "v2_crm_businesses",
    path: "/crm/businesses",
    label: "Organisations",
    icon: <Business />,
    navGroup: "people",
    navOrder: 12,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/businesses/BusinessesRoute"),
    ),
  },
  {
    id: "v2_crm_business_record",
    path: "/crm/businesses/:businessId",
    label: "Organisation",
    icon: <Business />,
    navGroup: "people",
    navOrder: 13,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/businesses/BusinessRecordRoute"),
    ),
  },
  {
    id: "v2_crm_firms",
    path: "/crm/firms",
    label: "Partners",
    icon: <Business />,
    navGroup: "people",
    navOrder: 14,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(() => import("@/vnext/routes/crm/firms/FirmsRoute")),
  },
  {
    id: "v2_crm_firm_record",
    path: "/crm/firms/:firmId",
    label: "Partner",
    icon: <Business />,
    navGroup: "people",
    navOrder: 15,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(() => import("@/vnext/routes/crm/firms/FirmRecordRoute")),
  },
  {
    id: "v2_crm_firm_clients",
    path: "/crm/firm-clients",
    label: "Partner-client support list",
    icon: <Business />,
    navGroup: "people",
    navOrder: 16,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/firm-clients/FirmClientsRoute"),
    ),
  },
  {
    id: "v2_crm_firm_client_record",
    path: "/crm/firm-clients/:relationshipId",
    label: "Partner-client relationship",
    icon: <Business />,
    navGroup: "people",
    navOrder: 17,
    navHidden: true,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/crm/firm-clients/FirmClientRecordRoute"),
    ),
  },
  {
    id: "user_access_audit",
    path: "/user-access-audit",
    label: "User access audit",
    icon: <Security />,
    navGroup: "people",
    navOrder: 18,
    navHidden: true,
    requiredPermission: "audit.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/people/user-access-audit/UserAccessAuditRoute"),
    ),
  },
  {
    id: "v2_forms",
    path: "/forms",
    label: "Forms",
    icon: <TrendingUp />,
    navGroup: "growth",
    navOrder: 20,
    requiredPermission: "forms.manage",
    Component: lazy(
      () => import("@/vnext/routes/growth/forms/GrowthFormsRoute"),
    ),
  },
  {
    id: "v2_form_builder",
    path: "/forms/:formId",
    label: "Form details",
    icon: <TrendingUp />,
    navGroup: "growth",
    navOrder: 20.1,
    navHidden: true,
    requiredPermission: "forms.manage",
    Component: lazy(
      () => import("@/vnext/routes/growth/forms/GrowthFormBuilderRoute"),
    ),
  },
  {
    id: "v2_audiences",
    path: "/audiences",
    label: "Audiences",
    icon: <TrendingUp />,
    navGroup: "growth",
    navOrder: 20.2,
    requiredPermission: "audiences.view",
    requiredPermissionFallbacks: ["segments.manage", "tags.manage"],
    Component: lazy(
      () => import("@/vnext/routes/growth/segments/GrowthSegmentsRoute"),
    ),
  },
  {
    id: "v2_audience_tag_library",
    path: "/audiences/tags",
    label: "Tags",
    icon: <TrendingUp />,
    navGroup: "growth",
    navOrder: 20.3,
    // Audiences owns tag governance, but the current backend permission
    // catalog still exposes the historical tag-management key.
    requiredPermission: "tags.manage",
    Component: lazy(() => import("@/vnext/routes/growth/tags/GrowthTagsRoute")),
  },
  {
    id: "v2_dashboard",
    path: "/dashboard",
    label: "Dashboard",
    icon: <Assessment />,
    navGroup: null,
    navOrder: 1,
    Component: lazy(() => import("@/vnext/routes/dashboard/DashboardRoute")),
  },
  {
    id: "v2_live_alerts",
    path: "/live-alerts",
    label: "Live Alerts Feed",
    icon: <NotificationsActiveOutlined />,
    navGroup: "support",
    navOrder: 29.9,
    requiredPermission: "admin.v2.access",
    Component: lazy(
      () => import("@/vnext/routes/support/live-alerts/LiveAlertsRoute"),
    ),
  },
  {
    id: "support_conversations",
    path: "/support/conversations",
    label: "Support conversations",
    icon: <SupportAgent />,
    navGroup: "support",
    navOrder: 30.01,
    requiredPermission: "support.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/support/conversations/SupportConversationsRoute"),
    ),
  },
  {
    id: "support_conversation_detail",
    path: "/support/conversations/:conversationId",
    label: "Support conversation",
    icon: <SupportAgent />,
    navGroup: "support",
    navOrder: 30.015,
    navHidden: true,
    requiredPermission: "support.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/support/conversations/SupportConversationDetailRoute"),
    ),
  },
  {
    id: "v2_issue_reports",
    path: "/issue-reports",
    // Shared queue for admin-portal and HelloCashflow app reports.
    // Legacy `/bug-tracker*` routes remain available only for older
    // monday-backed items and direct legacy links.
    label: adminUiCopy.supportRoutes.issueReports,
    icon: <BugReportOutlined />,
    navGroup: "support",
    navOrder: 30.04,
    Component: lazy(
      () => import("@/vnext/routes/support/issues/IssueReportsRoute"),
    ),
  },
  {
    id: "v2_issue_report_detail",
    path: "/issue-reports/:issueReportId",
    label: adminUiCopy.supportRoutes.issueReport,
    icon: <BugReportOutlined />,
    navGroup: "support",
    navOrder: 30.045,
    navHidden: true,
    Component: lazy(
      () => import("@/vnext/routes/support/issues/IssueReportDetailRoute"),
    ),
  },
  {
    id: "v2_support_help_centre",
    path: "/support/help-centre",
    label: "Help centre",
    icon: <MenuBook />,
    navGroup: "learning",
    navOrder: 71,
    navHidden: false,
    requiredPermission: "kb.manage",
    Component: lazy(
      () => import("@/vnext/routes/support/help-centre/HelpCentreRoute"),
    ),
  },
  {
    id: "v2_support_help_centre_article",
    path: "/support/help-centre/articles/:articleId",
    label: "Help Article",
    icon: <MenuBook />,
    navGroup: "learning",
    navOrder: 71.1,
    navHidden: true,
    requiredPermission: "kb.manage",
    Component: lazy(
      () => import("@/vnext/routes/support/help-centre/HelpCentreRoute"),
    ),
  },
  {
    id: "spoof",
    path: "/spoof",
    label: adminUiCopy.supportRoutes.logInAsUser,
    icon: <LockOpen />,
    navGroup: "support",
    navOrder: 30.4,
    requiredPermission: "support.spoof_user",
    Component: lazy(() => import("@/vnext/routes/support/spoof/SpoofRoute")),
  },
  {
    id: "advanced_spoof",
    path: "/advanced-spoof",
    label: adminUiCopy.supportRoutes.choosePreviewBuild,
    icon: <LockOpen />,
    navGroup: "support",
    navOrder: 30.45,
    navHidden: true,
    requiredPermission: "support.spoof_user",
    Component: lazy(
      () => import("@/vnext/routes/support/advanced-spoof/AdvancedSpoofRoute"),
    ),
  },
  {
    id: "spoof_sessions_audit",
    path: "/spoof-sessions",
    label: adminUiCopy.supportRoutes.accessSessionAudit,
    icon: <LockOpen />,
    navGroup: "support",
    navOrder: 30.5,
    navHidden: true,
    requiredPermission: "support.spoof_user",
    Component: lazy(
      () =>
        import("@/vnext/routes/support/spoof-sessions/SpoofSessionsAuditRoute"),
    ),
  },
  {
    id: "spoof_launcher",
    path: "/spoof-launcher",
    label: adminUiCopy.supportRoutes.startSupportSession,
    icon: <LockOpen />,
    navGroup: "support",
    navOrder: 30.55,
    navHidden: true,
    requiredPermission: "support.spoof_user",
    Component: lazy(
      () => import("@/vnext/routes/support/spoof-launcher/SpoofLauncherRoute"),
    ),
  },
  {
    id: "support_test_reset",
    path: "/test-reset",
    label: adminUiCopy.supportRoutes.testDataReset,
    icon: <Security />,
    navGroup: "support",
    navOrder: 30.6,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () => import("@/vnext/routes/support/test-reset/TestResetRoute"),
    ),
  },
  {
    id: "support_preview_keep_list",
    path: "/preview-keep-list",
    label: adminUiCopy.supportRoutes.previewKeepList,
    icon: <Security />,
    navGroup: "support",
    navOrder: 30.7,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () =>
        import("@/vnext/routes/support/preview-keep-list/PreviewKeepListRoute"),
    ),
  },
  {
    id: "v2_email_comms",
    path: "/email-comms",
    label: "Email & Comms",
    icon: <Email />,
    navGroup: "growth",
    navOrder: 20.4,
    requiredPermission: "messaging.view",
    Component: lazy(
      () => import("@/vnext/routes/messaging/bulk-email/BulkEmailRoute"),
    ),
  },
  {
    id: "v2_email_comms_templates",
    path: "/email-comms/templates",
    label: "Templates",
    icon: <Email />,
    navGroup: "growth",
    navOrder: 20.41,
    navHidden: true,
    requiredPermission: "messaging.view",
    Component: lazy(
      () => import("@/vnext/routes/messaging/templates/TemplatesRoute"),
    ),
  },
  {
    id: "v2_email_comms_activity",
    path: "/email-comms/activity",
    label: "Activity",
    icon: <Email />,
    navGroup: "growth",
    navOrder: 20.42,
    navHidden: true,
    requiredPermission: "messaging.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/messaging/email-activity/EmailActivityRoute"),
    ),
  },
  {
    id: "v2_email_comms_settings",
    path: "/email-comms/settings",
    label: "Settings",
    icon: <Email />,
    navGroup: "growth",
    navOrder: 20.43,
    navHidden: true,
    requiredPermission: "messaging.manage_identities",
    Component: lazy(
      () =>
        import("@/vnext/routes/messaging/sender-identities/SenderIdentitiesRoute"),
    ),
  },
  {
    id: "v2_automations",
    path: "/automations",
    label: "Automation Centre",
    icon: <SubdirectoryArrowRight />,
    navGroup: "growth",
    navOrder: 20.5,
    requiredPermission: "automation.view",
    Component: lazy(
      () => import("@/vnext/routes/growth/automations/AutomationsRoute"),
    ),
  },
  {
    id: "v2_product",
    path: "/product",
    label: "Product",
    icon: <Assessment />,
    navGroup: "product",
    navOrder: 10,
    requiredPermission: "featureflags.manage",
    requiredPermissionFallbacks: ["entitlements.manage"],
    Component: lazy(() => import("@/vnext/routes/product/ProductRoute")),
  },
  {
    id: "v2_revenue",
    path: "/revenue",
    label: "Revenue offers and onboarding",
    icon: <CreditCard />,
    navGroup: "revenue",
    navOrder: 60,
    requiredPermission: "billing.view",
    requiredMenuVisibility: "revenue",
    Component: lazy(() => import("@/vnext/routes/revenue/RevenueOffersRoute")),
  },
  {
    id: "v2_revenue_billing_dashboards",
    path: "/revenue/billing",
    label: "Billing overview",
    icon: <CreditCard />,
    navGroup: "revenue",
    navOrder: 60.1,
    navHidden: false,
    requiredPermission: "billing.view",
    requiredMenuVisibility: "revenue",
    Component: lazy(() => import("@/vnext/routes/revenue/RevenueBillingRoute")),
  },
  {
    id: "subscription",
    path: "/subscription",
    label: "Subscription tools",
    icon: <CreditCard />,
    navGroup: "revenue",
    navOrder: 60.12,
    requiredPermission: "billing.view",
    requiredMenuVisibility: "revenue",
    Component: lazy(
      () => import("@/vnext/routes/revenue/subscription/SubscriptionRoute"),
    ),
  },
  {
    id: "quickbooks_trials",
    path: "/quickbooks-trials",
    label: "QuickBooks trials",
    icon: <CreditCard />,
    navGroup: "revenue",
    navOrder: 60.15,
    requiredPermission: "billing.view",
    requiredMenuVisibility: "revenue",
    Component: lazy(
      () =>
        import("@/vnext/routes/revenue/quickbooks-trials/QuickBooksTrialsRoute"),
    ),
  },
  {
    id: "v2_revenue_coupons",
    path: "/revenue/coupons",
    label: "Coupons",
    icon: <CreditCard />,
    navGroup: "revenue",
    navOrder: 60.2,
    navHidden: false,
    requiredPermission: "billing.view",
    requiredMenuVisibility: "revenue",
    Component: lazy(() => import("@/vnext/routes/revenue/CouponsRoute")),
  },
  {
    id: "v2_revenue_affiliates",
    path: "/revenue/affiliates",
    label: "Affiliates",
    icon: <CreditCard />,
    navGroup: "revenue",
    navOrder: 60.3,
    navHidden: false,
    requiredPermission: "billing.view",
    requiredMenuVisibility: "revenue",
    Component: lazy(() => import("@/vnext/routes/revenue/AffiliatesRoute")),
  },
  {
    id: "v2_settings_webhook_logs",
    path: "/settings/webhook-logs",
    label: "Webhook logs",
    icon: <Assessment />,
    navGroup: "settings",
    navOrder: 48,
    requiredPermission: "billing.view",
    Component: WebhookLogsRoute,
  },
  {
    id: "v2_learning",
    path: "/learning",
    label: "Learning",
    icon: <MenuBook />,
    navGroup: "learning",
    navOrder: 70,
    requiredPermission: "learning.view",
    requiredMenuVisibility: "learning",
    Component: lazy(() => import("@/vnext/routes/learning/LearningRoute")),
  },
  {
    id: "help_videos",
    path: "/learning/help-videos",
    label: "Help videos",
    icon: <OndemandVideoOutlined />,
    navGroup: "learning",
    navOrder: 70.5,
    requiredPermission: "learning.view",
    Component: lazy(
      () => import("@/vnext/routes/learning/help-videos/HelpVideosRoute"),
    ),
  },
  {
    id: "help_video_details",
    path: "/learning/help-videos/:videoSlug",
    label: "Help video",
    icon: <OndemandVideoOutlined />,
    navGroup: "learning",
    navOrder: 70.6,
    navHidden: true,
    requiredPermission: "learning.view",
    Component: lazy(
      () => import("@/vnext/routes/learning/help-videos/HelpVideoDetailRoute"),
    ),
  },
  {
    id: "help_video_suggestions",
    path: "/learning/help-video-suggestions",
    label: "Video suggestions",
    icon: <OndemandVideoOutlined />,
    navGroup: "learning",
    navOrder: 70.7,
    requiredPermission: "learning.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/learning/help-videos/HelpVideoSuggestionsRoute"),
    ),
  },
  {
    id: "help_video_suggestion_details",
    path: "/learning/help-video-suggestions/:suggestionId",
    label: "Video suggestion",
    icon: <OndemandVideoOutlined />,
    navGroup: "learning",
    navOrder: 70.8,
    navHidden: true,
    requiredPermission: "learning.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/learning/help-videos/HelpVideoSuggestionDetailRoute"),
    ),
  },
  {
    id: "v2_sales",
    path: "/sales",
    label: "Sales workspace",
    icon: <People />,
    navGroup: "sales",
    navOrder: 80,
    requiredPermission: "crm.view",
    Component: lazy(() => import("@/vnext/routes/sales/SalesRoute")),
  },
  {
    id: "v2_insights_integrations",
    path: "/insights-integrations",
    label: "Insights",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90,
    requiredPermission: "analytics.view",
    Component: lazy(() => import("@/vnext/routes/insights/InsightsRoute")),
  },
  {
    id: "statistics",
    path: "/statistics",
    label: "Analytics reports",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.1,
    requiredPermission: "analytics.view",
    Component: lazy(
      () => import("@/vnext/routes/insights/statistics/StatisticsRoute"),
    ),
  },
  {
    id: "admin_metrics",
    path: "/admin-metrics",
    label: "Platform metrics",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.2,
    requiredPermission: "analytics.view",
    Component: lazy(
      () => import("@/vnext/routes/insights/admin-metrics/AdminMetricsRoute"),
    ),
  },
  {
    id: "usage_analytics",
    path: "/usage-analytics",
    label: "Usage analytics",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.3,
    requiredPermission: "analytics.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/insights/usage-analytics/UsageAnalyticsRoute"),
    ),
  },
  {
    id: "interaction_analytics",
    path: "/interaction-analytics",
    label: "Interaction analytics",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.31,
    requiredPermission: "analytics.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/insights/interaction-analytics/InteractionAnalyticsRoute"),
    ),
  },
  {
    id: "ai_insights_quality",
    path: "/ai-insights-quality",
    label: "AI Insights quality",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.32,
    requiredPermission: "analytics.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/insights/ai-insights-quality/AiInsightsQualityRoute"),
    ),
  },
  {
    id: "openai_usage",
    path: "/openai-usage",
    label: "OpenAI usage",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.33,
    requiredPermission: "analytics.view",
    Component: lazy(
      () => import("@/vnext/routes/insights/openai-usage/OpenAiUsageRoute"),
    ),
  },
  {
    id: "onboarding_analytics",
    path: "/onboarding-analytics",
    label: "Onboarding analytics",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.35,
    requiredPermission: "analytics.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/insights/onboarding-analytics/OnboardingAnalyticsRoute"),
    ),
  },
  {
    // FHC slice 1: gated on CRM view, because every card opens Contacts.
    id: "entry_points",
    path: "/entry-points",
    label: "Entry points",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.38,
    requiredPermission: "crm.view",
    Component: lazy(
      () => import("@/vnext/routes/insights/entry-points/EntryPointsRoute"),
    ),
  },
  {
    id: "partner_stats",
    path: "/partner-stats",
    label: "Partner stats",
    icon: <Insights />,
    navGroup: "insights",
    navOrder: 90.4,
    requiredPermission: "analytics.view",
    Component: lazy(
      () => import("@/vnext/routes/insights/partner-stats/PartnerStatsRoute"),
    ),
  },
  {
    id: "system_logs",
    path: "/logs",
    label: "System logs",
    icon: <Assessment />,
    navGroup: "settings",
    navOrder: 30,
    requiredPermission: "audit.view",
    Component: lazy(
      () => import("@/vnext/routes/ops-admin/system-logs/SystemLogsRoute"),
    ),
  },
  {
    id: "platform_status",
    path: "/platform-status",
    label: "Platform status",
    icon: <NotificationsActiveOutlined />,
    navGroup: "settings",
    navOrder: 29.9,
    requiredPermission: "analytics.view",
    Component: lazy(
      () => import("@/vnext/routes/ops-admin/status/StatusRoute"),
    ),
  },
  {
    id: "cashflow_data_quality",
    path: "/cashflow-data-quality",
    label: "Cashflow data quality",
    icon: <Assessment />,
    navGroup: "settings",
    navOrder: 31,
    requiredPermission: "audit.view",
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/cashflow-data-quality/CashflowDataQualityRoute"),
    ),
  },
  {
    id: "release_review",
    path: "/release-review",
    label: "Release review",
    icon: <SubdirectoryArrowRight />,
    navGroup: "settings",
    navOrder: 30.05,
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/release-review/ReleaseReviewRoute"),
    ),
  },
  // App/preview bug reporting is distinct from the admin-site issue queue at
  // `/issue-reports*`. Keep the route labels explicit so operators do not
  // log admin-portal issues into the wrong workflow.
  {
    id: "bug_tracker",
    path: "/bug-tracker",
    label: "App bug tracker (legacy)",
    icon: <BugReportOutlined />,
    navGroup: "support",
    navOrder: 31,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/bug-tracker/BugTrackerRecentRoute"),
    ),
  },
  {
    id: "bug_tracker_recent",
    path: "/bug-tracker/recent",
    label: "App bug reports (legacy)",
    icon: <BugReportOutlined />,
    navGroup: "support",
    navOrder: 31.1,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/bug-tracker/BugTrackerRecentRoute"),
    ),
  },
  {
    id: "bug_tracker_new",
    path: "/bug-tracker/new",
    label: "Log app bug (legacy)",
    icon: <BugReportOutlined />,
    navGroup: "support",
    navOrder: 31.15,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () => import("@/vnext/routes/ops-admin/bug-tracker/BugTrackerRoute"),
    ),
  },
  {
    id: "bug_tracker_details",
    path: "/bug-tracker/:bugReportId",
    label: "App bug report (legacy)",
    icon: <BugReportOutlined />,
    navGroup: "support",
    navOrder: 31.2,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () => import("@/vnext/routes/ops-admin/bug-tracker/BugTrackerRoute"),
    ),
  },
  // Feature Requests is the improvements/new-capabilities workflow. Keep it
  // distinct from app/preview defects (`/bug-tracker*`) and admin-portal
  // issues (`/issue-reports*`) so operators land in the right queue.
  {
    id: "feature_requests",
    path: "/feature-requests",
    label: "Feature requests",
    icon: <AutoAwesomeOutlined />,
    navGroup: "support",
    navOrder: 32,
    requiredPermission: "support.manage",
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/feature-requests/FeatureRequestsRoute"),
    ),
  },
  {
    id: "feature_requests_recent",
    path: "/feature-requests/recent",
    label: "Recent feature requests",
    icon: <AutoAwesomeOutlined />,
    navGroup: "support",
    navOrder: 32.1,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/feature-requests/FeatureRequestsRecentRoute"),
    ),
  },
  {
    id: "feature_requests_details",
    path: "/feature-requests/:featureRequestId",
    label: "Feature Request",
    icon: <AutoAwesomeOutlined />,
    navGroup: "support",
    navOrder: 32.2,
    navHidden: true,
    requiredPermission: "support.manage",
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/feature-requests/FeatureRequestsRoute"),
    ),
  },
  // Transcript Intake starts from raw call transcripts and hands proposals
  // into App Bug Tracker or Feature Requests. It is not the same as either
  // direct-entry workflow and stays separate from admin-portal issues.
  {
    id: "transcript_intake",
    path: "/transcript-intake",
    label: "Transcript intake",
    icon: <TextSnippet />,
    navGroup: "support",
    navOrder: 33,
    requiredPermission: "support.manage",
    Component: lazy(
      () =>
        import("@/vnext/routes/ops-admin/transcript-intake/TranscriptIntakeRoute"),
    ),
  },
  // Xero referral audit stays on the clean `/xero` path, but now lives on
  // the rebuilt Settings shell instead of the previous route bridge.
  {
    id: "xero_referral_audit",
    path: "/xero",
    label: "Xero referral audit",
    icon: <AccountBalance />,
    navGroup: "settings",
    navOrder: 37,
    requiredPermission: "integrations.view",
    Component: lazy(() => import("@/vnext/routes/settings/xero/XeroRoute")),
  },
  {
    id: "v2_team_permissions",
    path: "/settings/team-permissions",
    label: "Team and permissions",
    icon: <Security />,
    navGroup: "settings",
    navOrder: 10,
    requiredPermission: "rbac.manage",
    Component: TeamPermissionsRoute,
  },
  {
    id: "v2_approvals",
    path: "/settings/approvals",
    label: "Approvals",
    icon: <LockOpen />,
    navGroup: "settings",
    navOrder: 20,
    requiredPermission: "approvals.view",
    Component: ApprovalsRoute,
  },
  {
    id: "v2_audit_log",
    path: "/settings/audit-log",
    label: "Audit log",
    icon: <Assessment />,
    navGroup: "settings",
    navOrder: 30,
    requiredPermission: "audit.view",
    Component: AuditLogRoute,
  },
  {
    id: "admin_access_audit",
    path: "/admin-access",
    label: "Admin access audit",
    icon: <Security />,
    navGroup: "settings",
    navOrder: 35,
    requiredPermission: "audit.view",
    Component: AdminAccessAuditRoute,
  },
  {
    id: "business_types",
    path: "/admin/business-types",
    label: "Business types",
    icon: <Business />,
    navGroup: "settings",
    navOrder: 35.5,
    requiredPermission: "kb.manage",
    Component: BusinessTypesRoute,
  },
  {
    id: "docs_share_links",
    path: "/admin/docs-share-links",
    label: "Document share links",
    icon: <MenuBook />,
    navGroup: "settings",
    navOrder: 36,
    requiredPermission: "kb.manage",
    Component: DocsShareLinksRoute,
  },
  {
    id: "system_architecture",
    path: "/admin/system-architecture",
    label: "System architecture",
    icon: <AccountTreeOutlined />,
    navGroup: "settings",
    navOrder: 36.05,
    requiredPermission: "admin.v2.access",
    Component: SystemArchitectureRoute,
  },
  {
    id: "csv_exports",
    path: "/admin/csv-exports",
    label: "CSV exports",
    icon: <TextSnippet />,
    navGroup: "settings",
    navOrder: 36.1,
    requiredPermission: "exports.run",
    Component: CsvExportsRoute,
  },
  {
    id: "guides",
    path: "/admin/guides",
    label: "Guides",
    icon: <MenuBook />,
    navGroup: "settings",
    navOrder: 36.25,
    requiredPermission: "kb.manage",
    Component: GuidesRoute,
  },
  {
    id: "guide_details",
    path: "/admin/guides/:guideId",
    label: "Guide",
    icon: <MenuBook />,
    navGroup: "settings",
    navOrder: 36.3,
    navHidden: true,
    requiredPermission: "kb.manage",
    Component: GuidesRoute,
  },
  {
    // Content-block editing stays hidden in nav, but it now lives on the
    // rebuilt Settings shell under the Guides lineage rather than the
    // previous V2 bridge. See docs/guides/HOVER_HELP.md.
    id: "editable_content_block",
    path: "/EditableContentBlock/:contentId",
    label: "Content block editor",
    icon: <MenuBook />,
    navGroup: "settings",
    navOrder: 36.35,
    navHidden: true,
    requiredPermission: "kb.manage",
    Component: EditableContentBlockRoute,
  },
  {
    id: "demo_access",
    path: "/admin/demo-access",
    label: "Demo access",
    icon: <LockOpen />,
    navGroup: "settings",
    navOrder: 36.5,
    requiredPermission: "support.manage",
    Component: DemoAccessRoute,
  },
  {
    id: "demo_ops",
    path: "/admin/demo-ops",
    label: "Demo operations",
    icon: <Insights />,
    navGroup: "settings",
    navOrder: 36.75,
    requiredPermission: "support.manage",
    Component: DemoOpsRoute,
  },
  {
    id: "v2_jobs_monitor",
    path: "/settings/jobs",
    label: "Job monitor",
    icon: <TrendingUp />,
    navGroup: "settings",
    navOrder: 40,
    requiredPermission: "jobs.view",
    Component: JobsRoute,
  },
  {
    id: "v2_ai_insights_access",
    path: "/settings/ai-insights",
    label: "AI insights access",
    icon: <AutoAwesomeOutlined />,
    navGroup: "product",
    navOrder: 44.5,
    requiredPermission: "entitlements.manage",
    Component: AiInsightsRoute,
  },
  {
    id: "v2_feature_flags",
    path: "/settings/feature-flags",
    label: "Feature flags",
    icon: <Security />,
    navGroup: "product",
    navOrder: 45,
    // The Feature flags console manages entitlement-backed feature rollout
    // (apps/admin/src/vnext/routes/settings/feature-flags/FeatureFlagsRoute.tsx),
    // so it shares the entitlements permission rather than the legacy
    // feature_flags-table permission. Product dev staff may still open
    // the existing readiness workflow so the Product workspace does not
    // route them to an empty overview.
    requiredPermission: "entitlements.manage",
    requiredPermissionFallbacks: ["featureflags.manage"],
    Component: FeatureFlagsRoute,
  },
  {
    id: "v2_feature_flags_product_alias",
    path: "/product/feature-flags",
    label: "Feature flags",
    icon: <Security />,
    navGroup: "product",
    navOrder: 45.1,
    navHidden: true,
    requiredPermission: "entitlements.manage",
    requiredPermissionFallbacks: ["featureflags.manage"],
    Component: FeatureFlagsRoute,
  },
  {
    id: "v2_entitlements",
    path: "/settings/entitlements",
    label: "Entitlements",
    icon: <Security />,
    navGroup: "product",
    navOrder: 46,
    requiredPermission: "entitlements.manage",
    Component: EntitlementsRoute,
  },
  {
    id: "v2_settings_integrations",
    path: "/settings/integrations",
    label: "Integrations",
    icon: <AccountBalance />,
    navGroup: "settings",
    navOrder: 47,
    requiredPermission: "integrations.view",
    Component: IntegrationsRoute,
  },
];

export const ADMIN_ROUTES: AdminRouteDefinition[] = RAW_ADMIN_ROUTES;

export const isAdminV2Route = (route: AdminRouteDefinition): boolean => {
  return route.id.startsWith("v2_");
};

export interface AdminRouteAccessArgs {
  adminV2FoundationsEnabled?: boolean;
  permissionKeys?: ReadonlySet<string>;
  menuVisibility?: Partial<Record<AdminV2RevenueMenuVisibilityKey, boolean>>;
}

export interface AdminRouteAccessResult {
  canAccess: boolean;
  unavailableReason?: string;
}

export const findAdminRouteByPathname = (
  pathname: string,
): AdminRouteDefinition | undefined => {
  return ADMIN_ROUTES.find((route) =>
    matchPath({ path: route.path, end: true }, pathname),
  );
};

export const getNavGroupIdForPathname = (
  pathname: string,
): AdminNavGroupId | null => {
  return findAdminRouteByPathname(pathname)?.navGroup ?? null;
};

export const evaluateAdminRouteAccess = (
  route: AdminRouteDefinition,
  args?: AdminRouteAccessArgs,
): AdminRouteAccessResult => {
  if (isAdminV2Route(route) && !args?.adminV2FoundationsEnabled) {
    return {
      canAccess: false,
      unavailableReason: "This admin area is temporarily unavailable.",
    };
  }

  if (route.requiredPermission) {
    if (!args?.permissionKeys) {
      return {
        canAccess: false,
        unavailableReason: "Your access is still loading.",
      };
    }

    const acceptedPermissionKeys = [
      route.requiredPermission,
      ...(route.requiredPermissionFallbacks ?? []),
    ];

    if (
      !acceptedPermissionKeys.some((permissionKey) =>
        args.permissionKeys?.has(permissionKey),
      )
    ) {
      return {
        canAccess: false,
        unavailableReason: `Requires permission ${route.requiredPermission}.`,
      };
    }
  }

  if (
    route.requiredMenuVisibility &&
    !isRevenueMenuVisibilityEnabled(
      args?.menuVisibility,
      route.requiredMenuVisibility,
    )
  ) {
    return {
      canAccess: false,
      unavailableReason: `${route.requiredMenuVisibility} access is currently hidden for this account.`,
    };
  }

  return { canAccess: true };
};

const toKebabTitle = (value: string): string => {
  const normalized = value.replace(/[-_]+/g, " ").trim();
  return normalized.length === 0
    ? value
    : normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * Builds a breadcrumb list for the given pathname.
 *
 * @remarks
 * This prefers route labels from `ADMIN_ROUTES` (including pattern matches like
 * `/EditableContentBlock/:contentId`) and falls back to a simple kebab-to-title
 * transform for unknown segments.
 */
export const getBreadcrumbsForPathname = (
  pathname: string,
): Array<{ label: string; path?: string }> => {
  const rootRoute = ADMIN_ROUTES.find((route) => route.path === "/");
  const crumbs: Array<{ label: string; path?: string }> = [
    { label: rootRoute?.label ?? "Home", path: "/" },
  ];

  const segments = pathname.split("/").filter(Boolean);
  const prefixes = segments.map((_, index) => {
    return "/" + segments.slice(0, index + 1).join("/");
  });
  const activeRoute = ADMIN_ROUTES.find((route) =>
    matchPath({ path: route.path, end: true }, pathname),
  );
  const hiddenRouteParentBreadcrumbs: Partial<
    Record<string, Array<{ label: string; path: string }>>
  > = {
    editable_content_block: [
      { label: "Settings", path: "/admin/guides" },
      { label: "Guides", path: "/admin/guides" },
    ],
  };
  const activeRouteParentBreadcrumbs = activeRoute
    ? hiddenRouteParentBreadcrumbs[activeRoute.id]
    : undefined;
  let insertedActiveRouteParents = false;
  const pushCrumb = (label: string, path: string): void => {
    if (crumbs[crumbs.length - 1]?.label === label) {
      return;
    }
    crumbs.push({ label, path });
  };

  for (const prefix of prefixes) {
    if (prefix === "/") continue;

    const segment = prefix.split("/").filter(Boolean).slice(-1)[0];

    const matchingRoute = ADMIN_ROUTES.find((route) =>
      matchPath({ path: route.path, end: true }, prefix),
    );

    if (
      prefix === "/support" &&
      activeRoute?.path.startsWith("/support/help-centre")
    ) {
      pushCrumb("Learning", "/learning");
      continue;
    }

    if (!insertedActiveRouteParents && activeRouteParentBreadcrumbs) {
      activeRouteParentBreadcrumbs.forEach((parentCrumb) => {
        pushCrumb(parentCrumb.label, parentCrumb.path);
      });
      insertedActiveRouteParents = true;

      if (!matchingRoute) {
        continue;
      }
    }

    if (matchingRoute) {
      if (matchingRoute.navGroup === "advanced") {
        const advancedGroup = ADMIN_NAV_GROUPS.find(
          (group) => group.id === "advanced",
        );
        if (
          advancedGroup &&
          !crumbs.some((crumb) => crumb.label === advancedGroup.label)
        ) {
          pushCrumb(advancedGroup.label, "/admin");
        }
      }
      pushCrumb(matchingRoute.label, prefix);
      continue;
    }

    if (prefix === "/admin" && activeRoute?.navGroup) {
      const navGroup = ADMIN_NAV_GROUPS.find(
        (group) => group.id === activeRoute.navGroup,
      );
      if (navGroup) {
        pushCrumb(navGroup.label, prefix);
        continue;
      }
    }

    pushCrumb(toKebabTitle(segment), prefix);
  }

  return crumbs;
};

const NAV_GROUP_LANDING_ROUTE_IDS: Partial<Record<AdminNavGroupId, string>> = {
  people: "v2_crm",
  growth: "v2_email_comms",
  sales: "v2_sales",
  product: "v2_product",
  support: "v2_issue_reports",
  learning: "v2_learning",
  revenue: "v2_revenue",
  insights: "v2_insights_integrations",
  settings: "v2_team_permissions",
};

export const getNavGroupLandingPath = (
  navGroupId: AdminNavGroupId,
  args?: AdminRouteAccessArgs,
): string | undefined => {
  const preferredRouteId = NAV_GROUP_LANDING_ROUTE_IDS[navGroupId];
  const groupRoutes = ADMIN_ROUTES.filter(
    (route) => route.navGroup === navGroupId && !route.navHidden,
  ).sort((a, b) => {
    const aPreferred = a.id === preferredRouteId ? 1 : 0;
    const bPreferred = b.id === preferredRouteId ? 1 : 0;
    if (aPreferred !== bPreferred) {
      return bPreferred - aPreferred;
    }

    return a.navOrder - b.navOrder;
  });

  for (const route of groupRoutes) {
    const access = evaluateAdminRouteAccess(route, args);
    if (access.canAccess) {
      return route.path;
    }
  }

  return undefined;
};
